import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { credentialSchemasApi } from '@/api/credentialSchemas';
import SystemSettingsSection from '@/features/settings/SystemSettingsSection';
import TagManager from '@/features/settings/TagManager';
import { useAuthStore } from '@/stores/auth';
import { BgPreferencesSetting } from '@/features/settings/BgPreferencesSetting';
import { RevealTimeoutSetting } from '@/features/settings/RevealTimeoutSetting';
import { SchemaCard } from '@/features/settings/SchemaCard';
import { TransferCard } from '@/features/vault/transfer/TransferCard';

export default function SettingsPage() {
  const { t } = useTranslation();
  const { currentTenantId } = useAuthStore();
  const tenantId = currentTenantId;

  const schemasQuery = useQuery({
    queryKey: ['credential-schemas', tenantId],
    queryFn: () => credentialSchemasApi.list(tenantId!),
    enabled: !!tenantId,
    staleTime: 0,
  });

  return (
    <div>
      <div className="mb-6">
        <h1 className="display-title">{t('settings.title')}</h1>
        <p className="text-sm text-[var(--text-muted)] mt-1">
          {t('settings.subtitle')}
        </p>
      </div>

      <div className="space-y-4">
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="display-title-sm">{t('settings.credential_attributes')}</h2>
              <p className="text-xs text-[var(--text-muted)] mt-0.5">
                {t('admin_ui.settings.attributes_hint')}
              </p>
            </div>
          </div>

          {schemasQuery.isLoading && <p className="text-sm text-[var(--text-muted)]">{t('common.loading')}</p>}
          {schemasQuery.isError && (
            <p className="text-sm text-red-600">
              {t('common.error')}: {String(schemasQuery.error)}
            </p>
          )}

          <div className="space-y-2">
            {schemasQuery.data?.map((s) => (
              <SchemaCard key={s.type} schema={s} tenantId={tenantId!} />
            ))}
          </div>
        </div>

        {tenantId && (
          <div className="card">
            <div className="mb-4">
              <h2 className="display-title-sm">{t('tags_section.title')}</h2>
              <p
                className="text-xs mt-0.5"
                style={{ color: 'var(--text-muted)' }}
              >
                {t('tags_section.subtitle')}
              </p>
            </div>
            <TagManager tenantId={tenantId} />
          </div>
        )}

        <div className="card">
          <h2 className="display-title-sm mb-3">{t('admin_ui.settings.reveal_title')}</h2>
          <RevealTimeoutSetting />
        </div>

        <div className="card">
          <h2 className="display-title-sm mb-3">{t('admin_ui.settings.background_title')}</h2>
          <BgPreferencesSetting />
        </div>

        <TransferCard />

        <SystemSettingsSection />
      </div>
    </div>
  );
}
